// UI strings for "Mitä uutta" (shell/tour/: the tour on the next app open after the rising word pace and the
// grammar read twice, Axel 2026-10-14; docs/design/SANAPAJA.md "Alongside"), and its replay in Asetukset.
// The reminder and practice captions are the same for whoever sees them: nobody is named or singled out.
// Style: docs/FINNISH-STYLE.md (sinä; te for both, no jargon, only actionable text, dates "ke 21.10.").
import { defineArea } from '../area';

/** "noin viikon", "noin kahden viikon" … in the genitive, for "… välein". */
const FI_GEN = ['', '', 'kahden', 'kolmen', 'neljän', 'viiden', 'kuuden', 'seitsemän', 'kahdeksan', 'yhdeksän', 'kymmenen'];
const fiGen = (n: number) => FI_GEN[n] ?? String(n);

/** An interval in days as Finnish "noin puolentoista kuukauden välein" (weeks under five weeks, then half months). */
export function fiEvery(days: number): string {
  if (days < 35) {
    const w = Math.max(1, Math.round(days / 7));
    return w === 1 ? 'noin viikon välein' : `noin ${fiGen(w)} viikon välein`;
  }
  const halves = Math.max(2, Math.round((days / 30.44) * 2));
  const whole = Math.floor(halves / 2);
  const half = halves % 2 === 1;
  if (whole === 1) return half ? 'noin puolentoista kuukauden välein' : 'noin kuukauden välein';
  return half ? `noin ${fiGen(whole)} ja puolen kuukauden välein` : `noin ${fiGen(whole)} kuukauden välein`;
}

export function enEvery(days: number): string {
  if (days < 35) {
    const w = Math.max(1, Math.round(days / 7));
    return w === 1 ? 'about every week' : `about every ${w} weeks`;
  }
  const halves = Math.max(2, Math.round((days / 30.44) * 2));
  const months = halves / 2;
  if (months === 1) return 'about every month';
  if (months === 1.5) return 'about every month and a half';
  return `about every ${months} months`;
}

export const whatsNewFi = {
  title: 'Mitä uutta',
  next: 'Seuraava',
  done: 'Valmis',
  skip: 'Ohita',
  of: (n: number, total: number) => `${n}/${total}`,
  /** 1: the rate line as Tänään shows it, then the staircase in one line. */
  pace: {
    stairs: (days: number) => `Sanoja tulee vähän lisää ${fiEvery(days)}, kun muistatte ne hyvin.`,
    top: 'Tahti ei enää nouse tästä.',
  },
  /** 2: why it rises. */
  why: 'Mitä enemmän kielioppia ja sanoja jo osaatte, sitä helpompi uusia sanoja on oppia.',
  /** 3: the grammar read twice; `date` as "ke 21.10.". */
  grammar: {
    head: 'Viikon kielioppi luetaan nyt kahdesti.',
    door: 'Toinen lukukerta on nyt vuorossa: luet tekstin uudelleen ja vastaat muutamaan kysymykseen.',
    soon: (date: string) => `Toinen lukukerta on Tänään-sivulla ${date} alkaen: luet tekstin uudelleen ja vastaat muutamaan kysymykseen.`,
    later:
      'Tällä viikolla toinen lukukerta on jo vuorossa. Se on Tänään-sivulla päivän korttien jälkeen: luet tekstin uudelleen ja vastaat muutamaan kysymykseen.',
    waitsFirst: 'Toinen lukukerta ilmestyy Tänään-sivulle, kun olet lukenut viikon kieliopin Tunnilla.',
    done: 'Olet jo lukenut tämän viikon kieliopin kahdesti.',
  },
  /** 4: this device's reminders (shown only while they are not on here). */
  push: {
    what: 'Kun muistutukset ovat käytössä, saat ilmoituksen päivän korteista, jos et ole vielä opiskellut.',
    off: 'Tällä laitteella ne eivät ole käytössä.',
    enable: 'Ota muistutukset käyttöön',
    enableNote: 'Laite kysyy lupaa näyttää ilmoituksia. Valitse Salli.',
    on: 'Muistutukset ovat nyt käytössä tällä laitteella.',
    stillOff: 'Muistutukset eivät ole käytössä. Voit ottaa ne käyttöön myöhemmin Asetuksista.',
    nowDenied: 'Ilmoitukset on nyt estetty. Voit sallia ne myöhemmin laitteen asetuksista.',
    failed: 'Muistutuksia ei saatu käyttöön. Voit yrittää uudelleen Asetuksissa, kun verkkoyhteys toimii.',
    needsInstall:
      'iPhonessa muistutukset toimivat vain Koti-valikkoon lisätyssä sovelluksessa. Avaa sivu Safarissa, napauta Jaa ja valitse Lisää Koti-valikkoon. Avaa sitten sovellus Koti-valikon kuvakkeesta ja ota muistutukset käyttöön Asetuksista.',
    deniedIos: 'Ilmoitukset on estetty tältä sovellukselta. Voit sallia ne iPhonen asetuksista: Asetukset → Ilmoitukset → Valyrio udrir.',
    denied: 'Ilmoitukset on estetty tältä sivustolta. Voit sallia ne selaimen sivustoasetuksista.',
  },
  /** 5: the day's cards. */
  practice: (from: number, to: number) =>
    `Harjoituksessa teet Tänään-sivun päivän kortit. Niihin menee noin ${from}–${to} minuuttia. Sanat jäävät parhaiten mieleen, kun kortit tekee joka päivä.`,
  /** Asetukset: the replay. */
  settings: {
    title: 'Mitä uutta',
    replay: 'Näytä esittely uudelleen',
  },
};

export const whatsNewEn: typeof whatsNewFi = {
  title: 'What’s new',
  next: 'Next',
  done: 'Done',
  skip: 'Skip',
  of: (n, total) => `${n}/${total}`,
  pace: {
    stairs: (days) => `A few more words come ${enEvery(days)}, as long as you remember them well.`,
    top: 'The pace does not rise any further.',
  },
  why: 'The more grammar and words you already know, the easier new words are to learn.',
  grammar: {
    head: 'The week’s grammar is now read twice.',
    door: 'The second time is here: read the text again and answer a few questions.',
    soon: (date) => `The second time comes to Today from ${date}: read the text again and answer a few questions.`,
    later: 'This week the second time is already due. It is on Today after the day’s cards: read the text again and answer a few questions.',
    waitsFirst: 'The second time comes to Today once you have read the week’s grammar in the Lesson.',
    done: 'You have already read this week’s grammar twice.',
  },
  push: {
    what: 'With reminders on, the app reminds you of the day’s cards if you have not studied yet that day.',
    off: 'They are off on this device.',
    enable: 'Turn on reminders',
    enableNote: 'Your device asks permission to show notifications. Choose Allow.',
    on: 'Reminders are now on for this device.',
    stillOff: 'Reminders were not turned on. You can turn them on later in Settings.',
    nowDenied: 'Notifications were blocked. You can allow them later in the device settings.',
    failed: 'Could not turn on reminders. You can try again in Settings when you are online.',
    needsInstall:
      'On iPhone, reminders work only in the app added to the Home Screen. Open this page in Safari, tap Share and choose Add to Home Screen. Then open the app from its Home Screen icon and turn reminders on in Settings.',
    deniedIos: 'Notifications are blocked for this app. You can allow them in iPhone Settings: Settings → Notifications → Valyrio udrir.',
    denied: 'Notifications are blocked for this site. You can allow them in the browser’s site settings.',
  },
  practice: (from, to) =>
    `In Practice you do the day’s cards on Today. They take about ${from}–${to} minutes. Words stay best when the cards are done every day.`,
  settings: {
    title: 'What’s new',
    replay: 'Show the tour again',
  },
};

// Messages.whatsNew (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('whatsNew', whatsNewFi, whatsNewEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof whatsNewFi> {}
}
